/**
 * Hodet på første side: grå flate med tittel, artist, Voluplan og toneart.
 * Verdiene står her som data, så PDF-en og forhåndsvisningen tegner det samme hodet.
 */

import type jsPDF from 'jspdf';

export interface HeaderOptions {
  title: string;
  artist: string;
  transposedKey?: string;
}

type Rgb = [number, number, number];

export interface HodeTekst {
  tekst: string;
  side: 'venstre' | 'hoyre';
  marg: number; // mm fra den siden teksten er stilt mot
  y: number; // mm fra toppen, grunnlinjen for teksten
  storrelse: number; // punkt
  vekt: 'bold' | 'normal';
  farge: Rgb;
}

const MARG = 15;
const SVART: Rgb = [0, 0, 0];
const GRA: Rgb = [100, 100, 100];

export const HODE_FLATE: { hoyde: number; farge: Rgb } = { hoyde: 35, farge: [240, 240, 240] };

export function hodeTekster(options: HeaderOptions): HodeTekst[] {
  const venstre = { side: 'venstre', marg: MARG, farge: SVART } as const;
  const hoyre = { side: 'hoyre', marg: MARG, farge: GRA } as const;
  const tekster: HodeTekst[] = [
    { ...venstre, tekst: options.title, y: 12, storrelse: 18, vekt: 'bold' },
    { ...venstre, tekst: options.artist, y: 20, storrelse: 14, vekt: 'normal' },
    { ...hoyre, tekst: `Voluplan ${new Date().getFullYear()}`, y: 12, storrelse: 10, vekt: 'normal' },
  ];
  if (options.transposedKey) {
    tekster.push({ ...hoyre, tekst: `Toneart: ${options.transposedKey}`, y: 20, storrelse: 12, vekt: 'bold' });
  }
  return tekster;
}

/**
 * Render PDF header with title, artist, and metadata
 */
export function renderPDFHeader(
  doc: jsPDF,
  pageWidth: number,
  options: HeaderOptions
): void {
  doc.setFillColor(...HODE_FLATE.farge);
  doc.rect(0, 0, pageWidth, HODE_FLATE.hoyde, 'F');

  hodeTekster(options).forEach(({ tekst, side, marg, y, storrelse, vekt, farge }) => {
    doc.setFontSize(storrelse);
    doc.setFont('helvetica', vekt);
    doc.setTextColor(...farge);
    const x = side === 'venstre' ? marg : pageWidth - marg - doc.getTextWidth(tekst);
    doc.text(tekst, x, y);
  });

  // Reset color for content
  doc.setTextColor(...SVART);
}
